
import { useState, useEffect, useCallback } from 'react';
import { Criterion, InfoModule, Method, TenantConfig, Tenant, SubCriterion } from '../types';
import { getDocument, setDocument, getTenantCollectionPath, onSnapshot, doc, db, firebaseFunctions, httpsCallable } from '../services/firebaseService';
import { UserProfile } from '../types';
// INITIAL_* constants imports removed

/** hasSecondFactor: zweiter Faktor eingerichtet oder SSO-Konto (dort prüft der Provider die Identität) */
export const useCafData = (userProfile: UserProfile | null, isSuperAdmin: boolean, hasSecondFactor: boolean) => {
    // Initialize with empty defaults, will be populated by fetch
    const [cafStructure, setCafStructure] = useState<Criterion[]>([]);
    const [cafInfoModules, setCafInfoModules] = useState<InfoModule[]>([]);
    const [methods, setMethods] = useState<Method[]>([]);
    const [tenantConfig, setTenantConfig] = useState<TenantConfig>({ aiAnalysisEnabled: true, enablePrioritization: false });

    const [isLoading, setIsLoading] = useState<boolean>(true);
    // Routen hängen von Schaltern der Mandantenkonfiguration ab (z.B. Priorisierung): erst danach entscheiden
    const [isTenantConfigLoaded, setIsTenantConfigLoaded] = useState<boolean>(false);
    const [isTenantLocked, setIsTenantLocked] = useState(false);

    const tenantId = userProfile?.tenantId;
    const isTenantUser = !!tenantId && !isSuperAdmin;
    // Die Konfiguration geben die Regeln auch ohne zweiten Faktor heraus, alles Weitere des Mandanten erst mit
    // ihm, wenn die MFA-Pflicht gilt: darum zuerst die Konfiguration, dann die übrigen Daten
    const isTenantDataReadable = !isTenantUser || (isTenantConfigLoaded && (tenantConfig.enableMFA !== true || hasSecondFactor));

    // --- HYBRID DATA MERGE LOGIC ---
    // Merges the Global Template with Tenant Customizations
    const mergeStructures = useCallback((globalStr: Criterion[], tenantStr: Criterion[]): Criterion[] => {
        // 1. Deep clone global and mark as standard
        const merged: Criterion[] = globalStr.map(g => ({
            ...g,
            isStandard: true,
            subCriteria: g.subCriteria.map(s => ({
                ...s,
                isStandard: true,
                bestPractices: s.bestPractices.map(b => ({ ...b, isStandard: true }))
            }))
        }));

        if (!tenantStr || tenantStr.length === 0) return merged;

        // 2. Overlay Tenant Customizations
        tenantStr.forEach(tCrit => {
            const existingCrit = merged.find(m => m.id === tCrit.id);

            if (existingCrit) {
                // It's a standard criterion that might have custom sub-criteria
                tCrit.subCriteria.forEach(tSub => {
                    const existingSub = existingCrit.subCriteria.find(s => s.id === tSub.id);

                    if (existingSub) {
                        // It's a standard sub-criterion that might have custom Best Practices
                        if (tSub.bestPractices && tSub.bestPractices.length > 0) {
                            // Append custom BPs
                            existingSub.bestPractices = [
                                ...existingSub.bestPractices,
                                ...tSub.bestPractices // These won't have isStandard: true from step 1
                            ];
                        }
                    } else {
                        // It's a custom Sub-Criterion in a standard Criterion
                        existingCrit.subCriteria.push(tSub);
                    }
                });
            } else {
                // It's a completely custom Criterion (Theme)
                merged.push(tCrit);
            }
        });

        return merged;
    }, []);

    // Prepares the structure for saving by removing Standard items
    const extractTenantCustomizations = useCallback((fullStructure: Criterion[]): Criterion[] => {
        const tenantData: Criterion[] = [];

        fullStructure.forEach(crit => {
            if (!crit.isStandard) {
                // Entirely custom criterion, save as is
                tenantData.push(crit);
            } else {
                // Standard criterion, check for custom sub-criteria or custom best practices
                const customSubCriteria: SubCriterion[] = [];

                crit.subCriteria.forEach(sub => {
                    if (!sub.isStandard) {
                        // Entirely custom sub-criterion
                        customSubCriteria.push(sub);
                    } else {
                        // Standard sub-criterion, check for custom best practices
                        const customBPs = sub.bestPractices.filter(bp => !bp.isStandard);
                        if (customBPs.length > 0) {
                            // We need to save a "skeleton" of this sub-criterion
                            customSubCriteria.push({
                                id: sub.id,
                                title: sub.title, // Keep title for ref, though technically not needed if ID matches
                                bestPractices: customBPs,
                                // Don't save descriptions for standard items to save space/sync
                            });
                        }
                    }
                });

                if (customSubCriteria.length > 0) {
                    // We need to save a "skeleton" of this criterion
                    tenantData.push({
                        id: crit.id,
                        title: crit.title,
                        type: crit.type,
                        description: '', // Don't save desc
                        subCriteria: customSubCriteria
                    });
                }
            }
        });

        return tenantData;
    }, []);

    // Mandantenkonfiguration in Echtzeit, damit Änderungen des Admins sofort wirken
    useEffect(() => {
        if (!isTenantUser) return;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsTenantConfigLoaded(false);
        return onSnapshot(doc(db, `${getTenantCollectionPath(tenantId, 'settings')}/config`), (snap) => {
            if (snap.exists()) {
                setTenantConfig(prev => ({ ...prev, ...(snap.data() as TenantConfig) }));
            } else {
                setTenantConfig({ aiAnalysisEnabled: true, enablePrioritization: false });
            }
            setIsTenantConfigLoaded(true);
        }, (err) => {
            console.warn('[useCafData] tenantConfig snapshot error:', err);
            setIsTenantConfigLoaded(true);
        });
    }, [isTenantUser, tenantId]);

    // Data Loading
    useEffect(() => {
        let isMounted = true;
        let errorTimer: ReturnType<typeof setTimeout> | null = null;

        const fetchData = async () => {
            setIsLoading(true);
            // MFA-Pflicht ohne zweiten Faktor: nichts weiter laden, die App zeigt nur die Einrichtung
            if (!isTenantDataReadable) {
                if (isTenantConfigLoaded) setIsLoading(false);
                return;
            }

            try {
                // 1. LOAD GLOBAL DATA (Sequential to prevent Safari WebSocket/HTTP limits)
                const TIMEOUT_MS = 15000;
                
                // Wrapper to enforce a strict timeout per request, preventing silent hangs
                const fetchWithTimeout = async <T>(fetchFn: Promise<T>): Promise<T | null> => {
                    const timeoutPromise = new Promise<null>((_, reject) => {
                        setTimeout(() => reject(new Error("Data fetch timed out")), TIMEOUT_MS);
                    });
                    try {
                        return await Promise.race([fetchFn, timeoutPromise]);
                    } catch (e) {
                        console.error("Fetch failed or timed out:", e);
                        return null;
                    }
                };

                const globalInfoDoc = await fetchWithTimeout(getDocument<{ data: InfoModule[] }>('settings', 'cafInfo'));
                const globalMethodsDoc = await fetchWithTimeout(getDocument<{ data: Method[] }>('settings', 'methods'));
                const globalStructureDoc = await fetchWithTimeout(getDocument<{ data: Criterion[] }>('settings', 'global'));
                
                let tenantDoc: Tenant & { id: string } | null = null;
                let tenantStructureDoc: { data: Criterion[] } & { id: string } | null = null;
                
                let settingsPath = '';
                if (!isSuperAdmin && tenantId) {
                    tenantDoc = await fetchWithTimeout(getDocument<Tenant>('tenants', tenantId));
                    settingsPath = getTenantCollectionPath(tenantId, 'settings');
                    tenantStructureDoc = await fetchWithTimeout(getDocument<{ data: Criterion[] }>(settingsPath, 'cafStructure'));
                }

                // Default Global Data
                let globalStructure: Criterion[] = [];
                if (globalStructureDoc && globalStructureDoc.data) globalStructure = globalStructureDoc.data;

                if (isMounted) {
                    if (globalInfoDoc && globalInfoDoc.data) setCafInfoModules(globalInfoDoc.data);

                    if (globalMethodsDoc && globalMethodsDoc.data) {
                        setMethods(globalMethodsDoc.data);
                    } else {
                        // If no global methods document found (or error), we should NOT fall back to constants 
                        // if we want to support deleting them.
                        setMethods([]);
                    }
                }

                // 2. SUPER ADMIN VIEW (Raw Global Data)
                if (isSuperAdmin) {
                    if (isMounted) {
                        setCafStructure(globalStructure); // Super Admin sees/edits the raw global structure
                        setIsLoading(false);
                    }
                    return;
                }

                // 3. TENANT VIEW (Hybrid Merge)
                if (!tenantId) {
                    // Minimal delay to prevent flicker if tenantId loads shortly after
                    errorTimer = setTimeout(() => { if (isMounted) setIsLoading(false); }, 500);
                    return;
                }

                if (tenantDoc && tenantDoc.isActive === false) {
                    setIsTenantLocked(true);
                    if (isMounted) setIsLoading(false);
                    return;
                }

                if (!isMounted) return;

                const tenantCustomStructure = tenantStructureDoc?.data || [];
                const hybridStructure = mergeStructures(globalStructure, tenantCustomStructure);
                setCafStructure(hybridStructure);

            } catch (error) {
                console.error("Critical error loading data:", error);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
            if (errorTimer) clearTimeout(errorTimer);
        };
    }, [tenantId, isSuperAdmin, mergeStructures, isTenantDataReadable, isTenantConfigLoaded]);

    // Save Handlers
    const saveCafStructure = async (newStructure: Criterion[]): Promise<void> => {
        // Optimistic Update
        setCafStructure(newStructure);

        try {
            if (isSuperAdmin) {
                // Super Admin saves the GLOBAL standard directly
                await setDocument('settings', 'global', { data: newStructure });
            } else if (tenantId && userProfile) {
                // Tenant saves ONLY custom parts (Difference)
                const dataToSave = extractTenantCustomizations(newStructure);
                if (!firebaseFunctions) throw new Error("Firebase Functions not available.");
                const updateFn = httpsCallable(firebaseFunctions, 'updatecafstructure');
                await updateFn({ tenantId, cafStructure: dataToSave });
            }
        } catch (error) {
            console.error("Failed to save structure:", error);
            throw new Error(error instanceof Error ? error.message : 'errorSavingStructure', { cause: error });
        }
    };

    const saveCafInfoModules = async (newModules: InfoModule[]): Promise<void> => {
        setCafInfoModules(newModules);

        // ONLY SUPER ADMIN CAN SAVE INFO MODULES
        if (!isSuperAdmin) {
            return Promise.resolve();
        }

        try {
            await setDocument('settings', 'cafInfo', { data: newModules });
        } catch (error) {
            console.error("Failed to save info modules:", error);
            throw new Error(error instanceof Error ? error.message : 'errorSavingInfoModules', { cause: error });
        }
    };

    const saveMethods = async (newMethods: Method[]): Promise<void> => {
        setMethods(newMethods);

        if (!isSuperAdmin) return Promise.resolve();

        try {
            await setDocument('settings', 'methods', { data: newMethods });
        } catch (error) {
            console.error("Failed to save methods:", error);
            throw new Error(error instanceof Error ? error.message : 'errorSavingMethods', { cause: error });
        }
    };

    const saveTenantConfig = async (newConfig: TenantConfig): Promise<void> => {
        if (!tenantId || isSuperAdmin) return;
        const previousConfig = tenantConfig;
        setTenantConfig(newConfig); // Optimistic update
        try {
            const updateTenantConfigFn = httpsCallable(firebaseFunctions, 'updatetenantconfig');
            await updateTenantConfigFn({ tenantId, config: newConfig });
        } catch (error) {
            console.error("Failed to save settings:", error);
            // Abgelehnte Änderungen zurücknehmen: Eine lokal eingeschaltete MFA-Pflicht leitete sonst auf die Einrichtung um
            setTenantConfig(previousConfig);
            throw error;
        }
    };

    return {
        cafStructure,
        cafInfoModules,
        methods,
        tenantConfig,
        isLoading,
        isTenantConfigLoaded,
        isTenantLocked,
        saveCafStructure,
        saveCafInfoModules,
        saveMethods,
        saveTenantConfig
    };
};
